import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  setPersonalizationAgentTitle,
  setPersonalizationAgentType,
  setPersonalizationAgentProfileImport,
  resetPersonalizationAgentProfileImport,
} from '../../../../../../redux/slices/agents/agentSlice';
import {
  useGetCrmIntegrationsListQuery,
  useUploadTargetListCsvMutation,
  useImportProfilesFromCsvMutation,
  useGetCsvImportColumnsQuery,
  useImportProfilesFromHubspotMutation,
  useGetProfileImportStatusQuery,
  useGetPersonalizationProfileListQuery,
} from '../../../../../../services/api';

export const PROFILE_LIST_PAGE_SIZE = 25;

const usePersonalizationAgentDefineHandler = () => {
  const dispatch = useDispatch();
  const title = useSelector(state => state.agent.personalizationAgent.title);
  const type = useSelector(state => state.agent.personalizationAgent.type);
  const profileImport = useSelector(state => state.agent.personalizationAgent.profileImport);

  const [isSyncDrawerOpen, setIsSyncDrawerOpen] = useState(false);
  const [isConnectDrawerOpen, setIsConnectDrawerOpen] = useState(false);
  const [isCsvDrawerOpen, setIsCsvDrawerOpen] = useState(false);
  const [csvMediaId, setCsvMediaId] = useState(null);
  const [csvFileName, setCsvFileName] = useState('');
  const [selectedIntegrationHubId, setSelectedIntegrationHubId] = useState(null);
  const [pageNo, setPageNo] = useState(1);
  const [shouldPollStatus, setShouldPollStatus] = useState(true);

  const { data: crmIntegrationsResponse, isLoading: isCrmLoading } =
    useGetCrmIntegrationsListQuery();
  const crmIntegrations = crmIntegrationsResponse?.data || [];
  const hasHubspotConnection = crmIntegrations.length > 0;

  const [UPLOAD_CSV, { isLoading: isUploadingCsv }] = useUploadTargetListCsvMutation();
  const [IMPORT_CSV, { isLoading: isStartingCsvImport }] = useImportProfilesFromCsvMutation();
  const [IMPORT_HUBSPOT, { isLoading: isSyncingHubspot }] = useImportProfilesFromHubspotMutation();

  const {
    data: csvColumnsResponse,
    isFetching: isFetchingCsvColumns,
    isError: isCsvColumnsError,
  } = useGetCsvImportColumnsQuery(csvMediaId || skipToken);
  const csvColumnsPayload = csvColumnsResponse?.data ?? csvColumnsResponse;
  const csvColumns = Array.isArray(csvColumnsPayload)
    ? csvColumnsPayload
    : Array.isArray(csvColumnsPayload?.data)
      ? csvColumnsPayload.data
      : [];

  const importId = profileImport?.importId;
  const importSource = profileImport?.source || 'csv';
  const { data: statusResponse, isFetching: isFetchingStatus } = useGetProfileImportStatusQuery(
    importId ?? skipToken,
    { pollingInterval: importId && shouldPollStatus ? 2000 : 0 }
  );
  const importRecord = statusResponse?.data;
  const importStatus = importRecord?.status;
  const isImportCompleted = importStatus === 'completed';
  const isImporting = Boolean(importId) && !isImportCompleted;

  useEffect(() => {
    if (!importId) {
      setShouldPollStatus(true);
      return;
    }
    if (isImportCompleted) {
      setShouldPollStatus(false);
    }
  }, [importId, isImportCompleted]);

  const { data: listResponse, isFetching: isFetchingProfiles } =
    useGetPersonalizationProfileListQuery(
      importId && isImportCompleted
        ? {
            page: pageNo,
            pageSize: PROFILE_LIST_PAGE_SIZE,
            source: importSource,
            importId,
            search: '',
          }
        : skipToken
    );

  const profiles = (listResponse?.data?.data || []).map(profile => ({
    ...profile,
    _id: profile.id,
  }));
  const totalLength = listResponse?.data?.totalLength || 0;

  const handleTitleChange = event => {
    dispatch(setPersonalizationAgentTitle(event.target.value));
  };

  const handleTypeSelect = nextType => {
    dispatch(setPersonalizationAgentType(nextType));
  };

  const resetCsvDrawerState = () => {
    setCsvMediaId(null);
    setCsvFileName('');
  };

  const handleCsvFileSelect = useCallback(
    async file => {
      if (!file) return false;
      const isCsv = file.type === 'text/csv' || file.name?.toLowerCase().endsWith('.csv');
      if (!isCsv) {
        toast.error("Couldn't upload the file", { description: 'Choose a .csv file.' });
        return false;
      }

      try {
        const formData = new FormData();
        formData.append('file', file);
        const uploadResponse = await UPLOAD_CSV(formData).unwrap();
        const mediaId = uploadResponse?.data?._id;
        const fileName = uploadResponse?.data?.fileName || file.name;

        if (!mediaId) {
          toast.error("Couldn't upload the CSV", { description: 'Try uploading the file again.' });
          return false;
        }

        setCsvMediaId(mediaId);
        setCsvFileName(fileName);
        return true;
      } catch (error) {
        toast.error("Couldn't upload the CSV", {
          description: error?.data?.errorObject?.userErrorText || 'Try uploading the file again.',
        });
        return false;
      }
    },
    [UPLOAD_CSV]
  );

  const handleCsvImportConfirm = useCallback(
    async (selectedColumns = []) => {
      if (!csvMediaId || !selectedColumns.length) return false;

      try {
        const importResponse = await IMPORT_CSV({
          duplicateCondition: 'ignore',
          mediaId: csvMediaId,
          fileName: csvFileName,
          selectedColumns,
        }).unwrap();

        dispatch(
          setPersonalizationAgentProfileImport({
            source: 'csv',
            importId: importResponse?.data?.id || null,
            mediaId: csvMediaId,
            fileName: csvFileName,
            integrationId: null,
          })
        );
        setPageNo(1);
        setShouldPollStatus(true);
        setIsCsvDrawerOpen(false);
        resetCsvDrawerState();
        return true;
      } catch (error) {
        toast.error("Couldn't import the CSV", {
          description: error?.data?.errorObject?.userErrorText || 'Try importing the file again.',
        });
        return false;
      }
    },
    [IMPORT_CSV, csvFileName, csvMediaId, dispatch]
  );

  const openCsvDrawer = () => setIsCsvDrawerOpen(true);
  const closeCsvDrawer = () => {
    setIsCsvDrawerOpen(false);
    resetCsvDrawerState();
  };

  const openSyncDrawer = () => setIsSyncDrawerOpen(true);
  const closeSyncDrawer = () => setIsSyncDrawerOpen(false);
  const openConnectDrawer = () => setIsConnectDrawerOpen(true);
  const closeConnectDrawer = () => setIsConnectDrawerOpen(false);

  const handleSyncConfirm = async (selectedPropertiesToSync = []) => {
    if (!selectedIntegrationHubId) return;
    try {
      const response = await IMPORT_HUBSPOT({
        integrationId: selectedIntegrationHubId,
        selectedPropertiesToSync,
      }).unwrap();

      dispatch(
        setPersonalizationAgentProfileImport({
          source: 'hubspot',
          importId: response?.data?.id || null,
          mediaId: null,
          fileName: null,
          integrationId: selectedIntegrationHubId,
        })
      );
      setPageNo(1);
      setShouldPollStatus(true);
      closeSyncDrawer();
    } catch (error) {
      toast.error("Couldn't start the HubSpot sync", {
        description: error?.data?.errorObject?.userErrorText || 'Try syncing again.',
      });
    }
  };

  const handleResetImport = () => {
    setSelectedIntegrationHubId(null);
    setPageNo(1);
    setShouldPollStatus(true);
    dispatch(resetPersonalizationAgentProfileImport());
  };

  return {
    title,
    handleTitleChange,
    type,
    handleTypeSelect,
    hasHubspotConnection,
    isCrmLoading,
    crmIntegrations,
    profileImport,
    importStatus,
    isImporting,
    isFetchingStatus,
    profiles,
    totalLength,
    pageNo,
    setPageNo,
    pageSize: PROFILE_LIST_PAGE_SIZE,
    isFetchingProfiles,
    isUploadingCsv,
    isStartingCsvImport,
    isFetchingCsvColumns,
    isCsvColumnsError,
    isSyncingHubspot,
    handleCsvFileSelect,
    handleCsvImportConfirm,
    csvFileName,
    csvColumns,
    isCsvDrawerOpen,
    openCsvDrawer,
    closeCsvDrawer,
    isSyncDrawerOpen,
    openSyncDrawer,
    closeSyncDrawer,
    selectedIntegrationHubId,
    setSelectedIntegrationHubId,
    handleSyncConfirm,
    isConnectDrawerOpen,
    openConnectDrawer,
    closeConnectDrawer,
    handleResetImport,
  };
};

export default usePersonalizationAgentDefineHandler;
